import { useEffect, useRef, useState } from "react";

export function CustomMultiSelect({
    value = [],
    onChange,
    options = [],
    placeholder = "",
    disabled = false,
    filterPlaceholder = "Buscar...",
    emptyMessage = "Nenhuma opção a exibir",
}) {
    const [openUpward, setOpenUpward] = useState(false);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);
    const triggerRef = useRef(null);

    const selected = value ?? [];

    const filtered = options.filter((opt) =>
        opt.label?.toString().toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggle = (optValue) => {
        const exists = selected.includes(optValue);
        const next = exists
            ? selected.filter((v) => v !== optValue)
            : [...selected, optValue];
        onChange(next);
    };

    const removeChip = (e, optValue) => {
        e.stopPropagation();
        onChange(selected.filter((v) => v !== optValue));
    };

    const clearAll = (e) => {
        e.stopPropagation();
        onChange([]);
    };

    const selectedLabels = selected.map((v) => {
        const found = options.find((o) => o.value === v);
        return found ? found.label : v;
    });

    return (
        <div ref={containerRef} className="relative w-full">
            {/* Trigger */}
            <div
                ref={triggerRef}
                onClick={() => {
                    if (disabled) return;
                    if (!open) {
                        const rect = triggerRef.current?.getBoundingClientRect();
                        const spaceBelow = window.innerHeight - rect.bottom;
                        setOpenUpward(spaceBelow < 300); // 300px = altura estimada do dropdown
                    }
                    setOpen((o) => !o);
                }}
                className={`
                    min-h-[38px] w-full flex flex-wrap items-center gap-1 px-3 py-1.5
                    border rounded-lg shadow-xs cursor-pointer select-none
                    hover:shadow-sm
                    transition-all duration-150
                    ${disabled
                        ? "bg-muted border-border cursor-not-allowed opacity-60"
                        : "bg-card border-input hover:border-primary/50 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15"
                    }
                    ${open ? "border-primary ring-2 ring-primary/15" : ""}
                `}
            >
                {selected.length === 0 ? (
                    <span className="text-sm text-muted-foreground">{placeholder}</span>
                ) : (
                    selectedLabels.map((label, i) => (
                        <span
                            key={i}
                            className="inline-flex items-center gap-1 bg-accent text-accent-foreground text-xs font-medium px-2 py-0.5 rounded-full border border-primary/20"
                        >
                            {label}
                            <button
                                onClick={(e) => removeChip(e, selected[i])}
                                className="ml-0.5 text-primary/60 hover:text-primary leading-none"
                            >
                                ×
                            </button>
                        </span>
                    ))
                )}

                {/* Right icons */}
                <div className="ml-auto flex items-center gap-1 pl-1 shrink-0">
                    {selected.length > 0 && !disabled && (
                        <button
                            onClick={clearAll}
                            className="text-muted-foreground hover:text-foreground text-base leading-none"
                        >
                            ×
                        </button>
                    )}
                    <svg
                        className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            {/* Dropdown */}
            {open && (
                <div className={`absolute z-50 w-full surface-panel animate-in fade-in zoom-in-95 duration-150
                     ${openUpward ? "bottom-full mb-1" : "top-full mt-1"}`}>
                    {/* Search */}
                    <div className="p-2 border-b border-border">
                        <div className="relative">
                            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={filterPlaceholder}
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-input rounded-md bg-card focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                            />
                        </div>
                    </div>

                    {/* Select all / Clear */}
                    {filtered.length > 0 && (
                        <div className="flex gap-2 px-3 py-1.5 border-b border-border bg-muted/50">
                            <button
                                onClick={() => onChange(filtered.map((o) => o.value))}
                                className="text-xs text-primary hover:text-primary/80 font-medium"
                            >
                                Selecionar todos
                            </button>
                            <span className="text-border">|</span>
                            <button
                                onClick={() => onChange([])}
                                className="text-xs text-muted-foreground hover:text-foreground"
                            >
                                Limpar
                            </button>
                        </div>
                    )}

                    {/* Options */}
                    <ul className="max-h-52 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <li className="px-3 py-4 text-sm text-muted-foreground text-center">{emptyMessage}</li>
                        ) : (
                            filtered.map((opt) => {
                                const isSelected = selected.includes(opt.value);
                                return (
                                    <li
                                        key={opt.value}
                                        onClick={() => toggle(opt.value)}
                                        className={`
                                            flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors rounded-md mx-1
                                            ${isSelected ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-muted"}
                                        `}
                                    >
                                        <span className={`
                                            w-4 h-4 shrink-0 rounded border flex items-center justify-center transition-colors
                                            ${isSelected ? "bg-primary border-primary" : "border-input bg-card"}
                                        `}>
                                            {isSelected && (
                                                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </span>
                                        {opt.label}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}